import { useTranslation } from 'react-i18next';

import React, { forwardRef, useImperativeHandle, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@mui/material';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import { useTokens } from 'theme';

const GAP = 6;
const EDGE = 8;
const CHAT_VIEWPORT = '[data-chat-scroll="true"]';

function sameSelection(active, selection) {
  return Boolean(selection && !selection.isCollapsed &&
    selection.anchorNode === active.anchorNode && selection.anchorOffset === active.anchorOffset &&
    selection.focusNode === active.focusNode && selection.focusOffset === active.focusOffset &&
    active.area.contains(selection.anchorNode) && active.area.contains(selection.focusNode));
}

function placeButton(button, active) {
  const win = active.area.ownerDocument.defaultView;
  const screen = { left: EDGE, top: EDGE, right: win.innerWidth - EDGE, bottom: win.innerHeight - EDGE };
  const viewport = active.area.closest(CHAT_VIEWPORT)?.getBoundingClientRect();
  const bounds = viewport?.width > 0 && viewport?.height > 0 ? {
    left: Math.max(screen.left, viewport.left + EDGE), top: Math.max(screen.top, viewport.top + EDGE),
    right: Math.min(screen.right, viewport.right - EDGE), bottom: Math.min(screen.bottom, viewport.bottom - EDGE),
  } : screen;
  const width = button.offsetWidth || 110;
  const height = button.offsetHeight || 30;
  const measured = typeof active.range?.getBoundingClientRect === 'function' ? active.range.getBoundingClientRect() : null;
  const rect = measured?.height > 0 || measured?.width > 0 ? measured : active.point;
  const lastTop = Math.max(bounds.top, bounds.bottom - height);
  const lastLeft = Math.max(bounds.left, bounds.right - width);
  const bottom = Math.min(rect.bottom, bounds.bottom);
  const top = Math.max(rect.top, bounds.top);
  const preferred = bottom + GAP + height <= bounds.bottom ? bottom + GAP : top - height - GAP;
  button.style.left = `${Math.min(Math.max(rect.left, bounds.left), lastLeft)}px`;
  button.style.top = `${Math.min(Math.max(preferred, bounds.top), lastTop)}px`;
  button.style.visibility = 'visible';
}

/** Only this tiny portal updates; scrolling never rerenders the answer body. */
const SelectionReplyToolbar = forwardRef(function SelectionReplyToolbar({ onReply, messageId, role }, forwardedRef) {
  const { t: translate } = useTranslation();
  const t = useTokens();
  const [active, setActive] = useState(null);
  const buttonRef = useRef(null);
  useImperativeHandle(forwardedRef, () => ({ show: setActive }), []);

  useLayoutEffect(() => {
    if (!active) return undefined;
    const doc = active.area.ownerDocument;
    const win = doc.defaultView;
    let frame = null;
    const position = () => {
      frame = null;
      if (buttonRef.current) placeButton(buttonRef.current, active);
    };
    const schedule = () => {
      if (frame === null) frame = win.requestAnimationFrame(position);
    };
    const checkSelection = () => {
      if (!sameSelection(active, win.getSelection())) setActive(null);
      else schedule();
    };
    // A captured scroll only moves the portal in one pending animation frame.
    win.addEventListener('scroll', schedule, true);
    win.addEventListener('resize', schedule);
    doc.addEventListener('selectionchange', checkSelection);
    win.visualViewport?.addEventListener('resize', schedule);
    win.visualViewport?.addEventListener('scroll', schedule);
    const observer = typeof win.ResizeObserver === 'function' ? new win.ResizeObserver(schedule) : null;
    observer?.observe(active.area);
    const viewport = active.area.closest(CHAT_VIEWPORT);
    if (viewport) observer?.observe(viewport);
    // Sorting, paging and refetches can reuse the same selected text nodes.
    const mutations = typeof win.MutationObserver === 'function' ? new win.MutationObserver(() => setActive(null)) : null;
    mutations?.observe(active.area, { subtree: true, childList: true, characterData: true });
    position();
    return () => {
      if (frame !== null) win.cancelAnimationFrame(frame);
      win.removeEventListener('scroll', schedule, true);
      win.removeEventListener('resize', schedule);
      doc.removeEventListener('selectionchange', checkSelection);
      win.visualViewport?.removeEventListener('resize', schedule);
      win.visualViewport?.removeEventListener('scroll', schedule);
      observer?.disconnect();
      mutations?.disconnect();
    };
  }, [active]);

  if (!active) return null;
  return createPortal(
    <Button
      ref={buttonRef}
      size="small"
      variant="outlined"
      startIcon={<FormatQuoteIcon />}
      aria-label={translate('assistant.messagesReply')}
      data-reply-kind="selection"
      onPointerDown={(event) => event.preventDefault()}
      onMouseDown={(event) => event.preventDefault()}
      onClick={() => {
        const selection = active.area.ownerDocument.defaultView.getSelection();
        if (!sameSelection(active, selection)) { setActive(null); return; }
        const sourced = active.source && !['final', 'user'].includes(active.source.kind);
        const quote = sourced ? { text: active.quote, source: active.source, ...(active.proof ? { proof: active.proof } : {}), ...(active.label ? { label: active.label } : {}) } : active.quote;
        onReply({ messageId, role, text: active.quote, quote });
        selection.removeAllRanges();
        setActive(null);
      }}
      sx={{ position: 'fixed', top: 0, left: 0, visibility: 'hidden', zIndex: (theme) => theme.zIndex.tooltip, bgcolor: t.surface, fontSize: '0.75rem', py: 0.25, boxShadow: 1, '&:hover': { bgcolor: t.raised } }}
    >{translate('assistant.messagesReply2')}</Button>,
    active.area.ownerDocument.body,
  );
});

export default SelectionReplyToolbar;
